import {
  AppBar,
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import { Guitar, Chrome, DownloadCloud, Kanban, Keyboard as KeyboardIcon, LogIn, LogOut, Menu as MenuIcon, Music, Search, Users, X } from "lucide-react";
import React, { ReactNode, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import LoginModal from "./Components/LoginModal";
import ScrapeModal from "./Components/ScrapeModal";
import { ShortcutsModal } from "./Components/ShortcutsModal";
import { useUser } from "./utils/useUser";

interface LayoutProps {
  children: ReactNode;
}

interface NavigationItem {
  title: string;
  url: string;
  icon: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { user, isAuthenticated, logout, error: authError } = useUser();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [scrapeModalOpen, setScrapeModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  const navigationItems: NavigationItem[] = [
    {
      title: "Explore",
      url: "/Explore",
      icon: <Search size={18} />,
    },
    {
      title: "Artists",
      url: "/Artists",
      icon: <Users size={18} />,
    },
    {
      title: "My Songs",
      url: "/MySongs",
      icon: <Music size={18} />,
    },
    {
      title: "Lists",
      url: "/Kanban",
      icon: <Kanban size={18} />,
    },
    {
      title: "Chords",
      url: "/Chords",
      icon: <Guitar size={18} />,
    },
    {
      title: "Import",
      url: "/Import",
      icon: <DownloadCloud size={18} />,
    },
    {
      title: "Extension",
      url: "/extension",
      icon: <Chrome size={18} />,
    },
  ];

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
  };

  const isNavSelected = (itemUrl: string) => {
    if (itemUrl === "/Artists") {
      return location.pathname === "/Artists" || location.pathname.startsWith("/Artist");
    }
    if (itemUrl === "/Explore") {
      return location.pathname === "/Explore" || location.pathname === "/";
    }
    if (itemUrl === "/MySongs") {
      return location.pathname === "/MySongs" || location.pathname.startsWith("/EditSong") || location.pathname === "/my-songs";
    }
    if (itemUrl === "/Kanban") {
      return (
        location.pathname === "/Kanban" ||
        location.pathname === "/Playlists" ||
        location.pathname === "/MyLists"
      );
    }
    if (itemUrl === "/Import") {
      return location.pathname.toLowerCase().startsWith("/import") || location.pathname === "/Add";
    }
    if (itemUrl === "/extension") {
      return location.pathname === "/extension" || location.pathname === "/Extension";
    }
    return location.pathname === itemUrl;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "#ffffff",
      }}
    >
      {/* Top Navbar Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          position: { xs: "relative", md: "sticky" },
          bgcolor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid #e2e8f0",
          color: "text.primary",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="xl" disableGutters sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 52, sm: 60 },
              height: { xs: 52, sm: 60 },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Left: Brand / Logo */}
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1.5 }}>
              {/* Mobile menu toggle */}
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileDrawerOpen(true)}
                sx={{ display: { xs: "inline-flex", md: "none" }, p: 0.75 }}
                aria-label="Open Navigation Menu"
              >
                <MenuIcon size={22} />
              </IconButton>

              <Box
                component={Link}
                to="/Explore"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <Box
                  width={{ xs: 32, sm: 36 }}
                  height={{ xs: 32, sm: 36 }}
                  borderRadius={1.5}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    overflow: "hidden",
                    flexShrink: 0,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    border: "1px solid #e2e8f0",
                    bgcolor: "#ffffff",
                  }}
                >
                  <img
                    src="/logo192.png"
                    alt="ChordMaster"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="800"
                    sx={{
                      fontSize: { xs: "1.05rem", sm: "1.25rem" },
                      letterSpacing: "-0.5px",
                      lineHeight: 1.1,
                      background: "linear-gradient(90deg, #1e293b 0%, #2563eb 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    ChordMaster
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Desktop Navigation Links */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 1,
              }}
            >
              {navigationItems.map((item) => {
                const selected = isNavSelected(item.url);
                return (
                  <Button
                    key={item.title}
                    component={Link}
                    to={item.url}
                    startIcon={item.icon}
                    sx={{
                      px: 1.75,
                      py: 0.75,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: selected ? 700 : 500,
                      fontSize: "0.9rem",
                      color: selected ? "#1d4ed8" : "#475569",
                      bgcolor: selected ? "#eff6ff" : "transparent",
                      "&:hover": {
                        bgcolor: selected ? "#dbeafe" : "#f1f5f9",
                        color: "#1d4ed8",
                      },
                    }}
                  >
                    {item.title}
                  </Button>
                );
              })}
            </Box>

            {/* Right: User Profile / Auth (Removed redundant Import URL button) */}
            <Box display="flex" alignItems="center" gap={{ xs: 0.75, sm: 1.25 }}>
              {isAuthenticated && user ? (
                <Box sx={{ display: { xs: "none", md: "inline-flex" } }}>
                  <IconButton
                    size="small"
                    onClick={handleUserMenuOpen}
                    sx={{ p: 0.25, border: "2px solid #e2e8f0" }}
                    aria-label="User Account Menu"
                  >
                    <Avatar
                      src={user.avatarUrl}
                      sx={{ width: 30, height: 30, bgcolor: "primary.main", fontSize: "0.85rem" }}
                    >
                      {user.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Box>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<LogIn size={16} />}
                  onClick={() => setLoginModalOpen(true)}
                  sx={{
                    display: { xs: "none", md: "inline-flex" },
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    py: 0.5,
                    px: 1.5,
                  }}
                >
                  Sign In
                </Button>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: "#ffffff",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          p={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid #e2e8f0"
        >
          <Box display="flex" alignItems="center" gap={1.25}>
            <Box
              width={32}
              height={32}
              borderRadius={1.5}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                overflow: "hidden",
                flexShrink: 0,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                border: "1px solid #e2e8f0",
                bgcolor: "#ffffff",
              }}
            >
              <img
                src="/logo192.png"
                alt="ChordMaster"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
            <Typography variant="subtitle1" fontWeight="bold">
              ChordMaster
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setMobileDrawerOpen(false)}>
            <X size={20} />
          </IconButton>
        </Box>

        {/* Drawer Navigation Links */}
        <Box px={1.5} py={2} flex={1}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="bold"
            textTransform="uppercase"
            px={1.5}
            mb={1}
            display="block"
          >
            Navigation
          </Typography>
          <List disablePadding>
            {navigationItems.map((item) => {
              const selected = isNavSelected(item.url);
              return (
                <ListItem key={item.title} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to={item.url}
                    selected={selected}
                    onClick={() => setMobileDrawerOpen(false)}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      px: 1.5,
                      "&.Mui-selected": {
                        bgcolor: "#eff6ff",
                        color: "#1d4ed8",
                        fontWeight: 700,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34, color: selected ? "#1d4ed8" : "#64748b" }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: "0.95rem",
                        fontWeight: selected ? 700 : 500,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* Drawer Auth Footer */}
        <Box p={2} borderTop="1px solid #e2e8f0">
          {isAuthenticated && user ? (
            <Box
              onClick={handleUserMenuOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1,
                borderRadius: 2,
                bgcolor: "#f8fafc",
                cursor: "pointer",
                "&:hover": { bgcolor: "#f1f5f9" },
              }}
            >
              <Avatar src={user.avatarUrl} sx={{ width: 34, height: 34, bgcolor: "primary.main" }}>
                {user.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ overflow: "hidden", flex: 1 }}>
                <Typography variant="body2" fontWeight="bold" noWrap>
                  {user.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {user.email || user.id}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={<LogIn size={18} />}
              onClick={() => {
                setMobileDrawerOpen(false);
                setLoginModalOpen(true);
              }}
              sx={{ py: 1, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              Sign In
            </Button>
          )}
        </Box>
      </Drawer>

      {/* Main Page Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#ffffff",
          width: "100%",
        }}
      >
        {authError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => setLoginModalOpen(true)}>Sign in</Button>}>{authError}</Alert>}
        {children}
      </Box>

      {/* User Actions Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: { width: 220, borderRadius: 2, mt: 1, boxShadow: 6 },
        }}
      >
        {user && (
          <Box px={2} py={1} borderBottom="1px solid #f1f5f9">
            <Typography variant="subtitle2" fontWeight="bold">
              {user.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {user.email || user.id}
            </Typography>
          </Box>
        )}
        <ListItemButton component={Link} to="/MySongs" onClick={handleUserMenuClose}>
          <ListItemIcon>
            <Music size={18} />
          </ListItemIcon>
          <ListItemText primary="My Songs" />
        </ListItemButton>
        <ListItemButton component={Link} to="/Kanban" onClick={handleUserMenuClose}>
          <ListItemIcon>
            <Kanban size={18} />
          </ListItemIcon>
          <ListItemText primary="My Lists" />
        </ListItemButton>
        <MenuItem
          onClick={() => {
            handleUserMenuClose();
            setShortcutsModalOpen(true);
          }}
        >
          <ListItemIcon>
            <KeyboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Shortcuts (?)" />
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
          <ListItemIcon sx={{ color: "error.main" }}>
            <LogOut size={18} />
          </ListItemIcon>
          <ListItemText primary="Sign Out" />
        </MenuItem>
      </Menu>

      {/* Global Scrape / Import Modal */}
      <ScrapeModal open={scrapeModalOpen} onClose={() => setScrapeModalOpen(false)} />

      {/* Global Login Modal */}
      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />

      {/* Global Keyboard Shortcuts Modal */}
      <ShortcutsModal open={shortcutsModalOpen} onClose={() => setShortcutsModalOpen(false)} />
    </Box>
  );
}
